import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, Mail, KeyRound, Save } from 'lucide-react';
import { api, type System } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ExcelExportButton from '../components/ExcelExportButton';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

type CognitoUser = {
  username: string;
  email: string;
  sub: string;
  status: string; // CONFIRMED | FORCE_CHANGE_PASSWORD | RESET_REQUIRED
  enabled: boolean;
  created_at: string;
  // From our User entity (option-B). Null when entity record is missing.
  system_access: System[] | null;
  linked_agent_id: string | null;
};

const ALL_SYSTEMS: System[] = ['Messer', 'Empire-ACA'];

export default function Users() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState<{
    email: string;
    sendWelcome: boolean;
    linkedAgentId: string;
    systemAccess: Set<System>;
  }>({
    email: '',
    sendWelcome: false,
    linkedAgentId: '',
    systemAccess: new Set(['Messer']),
  });
  const [tempPasswordModal, setTempPasswordModal] = useState<{ email: string; tempPassword: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CognitoUser | null>(null);

  const usersQuery = useQuery({
    queryKey: ['cognito-users'],
    queryFn: () => api.listCognitoUsers(),
  });

  const agentsQuery = useQuery({
    queryKey: ['agents-all'],
    queryFn: () => api.listAgents(),
  });

  const handleCreate = async () => {
    if (!newUser.email.trim()) {
      toast.error('Email required');
      return;
    }
    try {
      const r = await api.createCognitoUser({
        email: newUser.email.trim(),
        send_welcome_email: newUser.sendWelcome,
        linked_agent_id: newUser.linkedAgentId || undefined,
        system_access: [...newUser.systemAccess],
      });
      if (r.temp_password) {
        setTempPasswordModal({ email: r.user.email, tempPassword: r.temp_password });
      } else {
        toast.success(`Welcome email sent to ${r.user.email}`);
      }
      setNewUser({
        email: '',
        sendWelcome: false,
        linkedAgentId: '',
        systemAccess: new Set(['Messer']),
      });
      setCreating(false);
      queryClient.invalidateQueries({ queryKey: ['cognito-users'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    } catch (err) {
      toast.error(`Create failed: ${(err as Error).message}`);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteCognitoUser(confirmDelete.username);
      toast.info(`Deleted ${confirmDelete.username}`);
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['cognito-users'] });
    } catch (err) {
      toast.error(`Delete failed: ${(err as Error).message}`);
    }
  };

  const handleResend = async (u: CognitoUser) => {
    try {
      await api.resendCognitoInvite(u.username);
      toast.success(`Welcome email re-sent to ${u.email}`);
    } catch (err) {
      toast.error(`Resend failed: ${(err as Error).message}`);
    }
  };

  if (usersQuery.isLoading) return <LoadingSpinner label="Loading users…" />;

  const users = (usersQuery.data?.users ?? []) as CognitoUser[];
  const agents = (agentsQuery.data?.agents ?? []).filter(
    (a) => a.agent_id !== 'unpaid' && a.agent_id !== 'unknown',
  );

  const linkedAgent = newUser.linkedAgentId
    ? agents.find((a) => a.agent_id === newUser.linkedAgentId) ?? null
    : null;

  const toggleNewSystem = (s: System) => {
    setNewUser((prev) => {
      const next = new Set(prev.systemAccess);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return { ...prev, systemAccess: next };
    });
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
            User Management
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Cognito accounts that can sign in. <strong>System access</strong> is per-user and lives
            on the User record — what the login can see in the portal. Linking to an Agent record
            attaches their commission history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {users.length > 0 && (
            <ExcelExportButton
              filename="users.xlsx"
              sheets={() => [
                {
                  name: 'Users',
                  rows: [
                    ['Email', 'Status', 'System access', 'Linked agent', 'Created', 'Cognito sub'],
                    ...users.map((u) => [
                      u.email,
                      u.status,
                      (u.system_access ?? []).join(', '),
                      u.linked_agent_id ?? '',
                      u.created_at,
                      u.sub,
                    ]),
                  ],
                },
              ]}
            />
          )}
          {!creating && (
            <button
              onClick={() => setCreating(true)}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <UserPlus size={14} /> Add user
            </button>
          )}
        </div>
      </div>

      {creating && (
        <div className="card space-y-3">
          <h3 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>
            New user
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                className="input text-sm"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="agent@example.com"
                autoFocus
              />
            </div>
            <div>
              <label className="label">Link to Agent record (optional)</label>
              <select
                className="select text-sm"
                value={newUser.linkedAgentId}
                onChange={(e) => {
                  const id = e.target.value;
                  // Pre-fill system access from the linked agent if user
                  // hasn't explicitly customized it.
                  const a = agents.find((x) => x.agent_id === id);
                  setNewUser((prev) => ({
                    ...prev,
                    linkedAgentId: id,
                    systemAccess: a?.system_access
                      ? new Set(a.system_access as System[])
                      : prev.systemAccess,
                  }));
                }}
              >
                <option value="">— don't link —</option>
                {agents.map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.display_name} ({a.system_access?.join(', ')})
                  </option>
                ))}
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                When linked, agent's <code>cognito_sub</code> + email update on save.
                {linkedAgent && (
                  <>
                    {' '}Linked agent's commission scope: {' '}
                    <span className="font-mono">
                      {linkedAgent.system_access?.join(', ') || '(none)'}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div>
            <label className="label">System access *</label>
            <div className="flex gap-3 flex-wrap">
              {ALL_SYSTEMS.map((s) => (
                <label
                  key={s}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                  style={{ color: 'var(--color-text)' }}
                >
                  <input
                    type="checkbox"
                    checked={newUser.systemAccess.has(s)}
                    onChange={() => toggleNewSystem(s)}
                  />
                  {s}
                </label>
              ))}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Which systems this user can see in the portal. Independent of agent-level commission
              scope.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text)' }}>
            <input
              type="checkbox"
              checked={newUser.sendWelcome}
              onChange={(e) => setNewUser({ ...newUser, sendWelcome: e.target.checked })}
            />
            Send welcome email now (uncheck to preview the account first; you'll get a temp
            password to copy/paste)
          </label>
          <div className="flex gap-2">
            <button onClick={handleCreate} className="btn btn-primary text-sm">
              Create user
            </button>
            <button
              onClick={() => {
                setCreating(false);
                setNewUser({
                  email: '',
                  sendWelcome: false,
                  linkedAgentId: '',
                  systemAccess: new Set(['Messer']),
                });
              }}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div className="card text-center py-12">
          <UserPlus
            size={32}
            className="mx-auto"
            style={{ color: 'var(--color-text-muted)' }}
          />
          <p className="mt-3" style={{ color: 'var(--color-text-muted)' }}>
            No users yet.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>System access</th>
                <th>Linked agent</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.username}
                  user={u}
                  onResend={() => handleResend(u)}
                  onDelete={() => setConfirmDelete(u)}
                  onSaved={() => queryClient.invalidateQueries({ queryKey: ['cognito-users'] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title={`Delete ${confirmDelete?.email}?`}
        message="Their Cognito account is removed. Any linked Agent record stays. Cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmModal
        open={!!tempPasswordModal}
        title={`User ${tempPasswordModal?.email} created`}
        message={`No welcome email was sent. Temp password: ${tempPasswordModal?.tempPassword}\n\nThe user must change it on first sign-in. Save this somewhere — Cognito won't show it again.`}
        confirmLabel="Got it"
        variant="default"
        onConfirm={() => {
          if (tempPasswordModal) {
            navigator.clipboard.writeText(tempPasswordModal.tempPassword).catch(() => {});
          }
          setTempPasswordModal(null);
        }}
        onCancel={() => setTempPasswordModal(null)}
      />
    </div>
  );
}

function UserRow({
  user,
  onResend,
  onDelete,
  onSaved,
}: {
  user: CognitoUser;
  onResend: () => void;
  onDelete: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState<Set<System> | null>(null);
  const initial = new Set<System>((user.system_access ?? []) as System[]);
  const current = editing ?? initial;

  const dirty = !setsEqual(current, initial);

  const toggle = (s: System) => {
    const next = new Set(editing ?? initial);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setEditing(next);
  };

  const save = async () => {
    if (!user.sub) return;
    try {
      await api.updateUserSystemAccess(user.sub, [...current]);
      toast.success(`Updated ${user.email}`);
      setEditing(null);
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  };

  return (
    <tr>
      <td className="font-medium">{user.email}</td>
      <td>
        <UserStatusBadge status={user.status} />
      </td>
      <td>
        {user.system_access === null ? (
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            (not migrated)
          </span>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            {ALL_SYSTEMS.map((s) => (
              <label
                key={s}
                className="flex items-center gap-1 text-xs cursor-pointer"
                style={{ color: 'var(--color-text)' }}
              >
                <input
                  type="checkbox"
                  checked={current.has(s)}
                  onChange={() => toggle(s)}
                />
                {s}
              </label>
            ))}
            {dirty && (
              <button
                onClick={save}
                className="btn btn-primary py-1 px-2 text-xs flex items-center gap-1"
              >
                <Save size={11} /> Save
              </button>
            )}
          </div>
        )}
      </td>
      <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        {user.linked_agent_id || '—'}
      </td>
      <td className="text-xs">{new Date(user.created_at).toLocaleDateString()}</td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={onResend} className="btn-icon" title="Re-send welcome email">
            <Mail size={14} />
          </button>
          <button onClick={onDelete} className="btn-icon text-red-500" title="Delete user">
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function UserStatusBadge({ status }: { status: string }) {
  if (status === 'CONFIRMED') {
    return <span className="badge badge-success text-xs">Confirmed</span>;
  }
  if (status === 'FORCE_CHANGE_PASSWORD') {
    return (
      <span className="badge badge-warning text-xs flex items-center gap-1">
        <KeyRound size={10} /> Pending first sign-in
      </span>
    );
  }
  if (status === 'RESET_REQUIRED') {
    return <span className="badge badge-warning text-xs">Reset required</span>;
  }
  return <span className="badge badge-gray text-xs">{status}</span>;
}
